/**
 * P2 return-confirmation + health-prearm helpers.
 *
 * The shared `api` export (api.js) is owned by the L8 legal-footer work and is
 * intentionally NOT modified here. We attach two extra helpers onto the same
 * `api` object so the rest of the app can use `api.getConfirm` / `api.prewarmHealth`
 * exactly as the launch plan describes, without touching api.js.
 *
 * Endpoints (served by the backend, same origin in prod / proxied in dev):
 *   GET /api/health             -> { ok: true, db: bool }
 *   GET /api/confirm/:sessionId -> { status: "pending" | "paid" | "unknown" }
 *
 * CRITICAL: confirmation status comes ONLY from the server. The client NEVER
 * trusts a `status` query param, never locally inserts/upserts a listing, and
 * never claims success before the server says "paid".
 */

import { api } from "@/api";

const API_BASE = process.env.REACT_APP_API_BASE || "";
const isDev = typeof process !== "undefined" && process.env && process.env.NODE_ENV !== "production";
const RESOLVED_BASE = API_BASE || (isDev ? "http://localhost:3001" : "");

/**
 * Fire-and-forget health prewarm. The backend spins up the DB pool / cold
 * server lazily; hitting /api/health before the user opens checkout "wakes the
 * uplink" so the real /api/checkout and /api/confirm calls are snappy.
 * Returns nothing and NEVER throws — failures here must not block the UI.
 */
export function prewarmHealth() {
  const url = `${RESOLVED_BASE}/api/health`;
  try {
    const p = fetch(url, { method: "GET", cache: "no-store" });
    // Swallow async transport failures — prewarm is strictly best-effort and must
    // NEVER surface as an unhandled rejection that could break a test or the UI.
    if (p && typeof p.catch === "function") p.catch(() => {});
  } catch {
    // intentionally swallowed — prewarm is best-effort.
  }
}

/**
 * Server-verified payment confirmation. Returns { status } where status is one
 * of "pending" | "paid" | "unknown". Throws only on transport/non-200 so the
 * caller can distinguish "still polling" from "the server itself is broken".
 */
export async function getConfirm(sessionId) {
  if (!sessionId) return { status: "unknown" };
  const res = await fetch(`${RESOLVED_BASE}/api/confirm/${encodeURIComponent(sessionId)}`, {
    method: "GET",
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`confirm failed: ${res.status}`);
  }
  const data = await res.json().catch(() => ({}));
  const status = data && typeof data.status === "string" ? data.status : "unknown";
  return { status };
}

// Attach to the shared api object so callers can use api.prewarmHealth / api.getConfirm.
api.prewarmHealth = prewarmHealth;
api.getConfirm = getConfirm;

export { api };
export default api;
